import { FC, useContext } from 'react';
import styled, { css, keyframes } from 'styled-components';

import PrimaryButton from '@/components/Buttons/PrimaryButton';
import { getImage } from '@/lib/fetchAPI';
import { parseHTML } from '@/lib/parse';
import { translate } from '@/lib/translate';
import { GlobalContext } from '@/pages/_app';

const Slide: FC<any> = ({ data, activeSlide, index }) => {
	const { lang } = useContext(GlobalContext);

	return (
		<OutsideWrapper
			$active={activeSlide}
			$current={index}
			className="outside-wrapper-hero"
		>
			<Wrapper
				$active={activeSlide}
				$current={index}
				className="slide-wrapper-hero"
			>
				<SlideItem>
					<SlideContainer>
						{data?.image ? (
							<SlideImages className="slide__images-wrapper-hero">
								<picture>
									<img
										src={getImage(data?.image)}
										alt={translate(lang, {
											en: 'Viber Services to Promote Your Business 1',
											ru: 'Сервисы Viber для продвижения вашего бизнеса 1',
											ua: 'Сервіси Viber для просування вашого бізнесу 1',
										})}
										className={
											'width' in data.image &&
											data?.image.width === 1920 &&
											data?.image.height === 1080
												? 'main-image' + data?.image.width
												: data?.image?.width === 1920 &&
													  data?.image?.height === 1080
													? 'main-image' + data?.image?.width
													: 'main-image'
										}
									/>
								</picture>

								{'id' in data.image && data?.image.id === 412 && (
									<picture>
										<MessageImage
											src="/images/homepage-slider/chat-min.png"
											alt={translate(lang, {
												en: 'Viber Services to Promote Your Business 2',
												ru: 'Сервисы Viber для продвижения вашего бизнеса 2',
												ua: 'Сервіси Viber для просування вашого бізнесу 2',
											})}
										/>
									</picture>
								)}
							</SlideImages>
						) : null}
					</SlideContainer>
				</SlideItem>
			</Wrapper>

			<SlideContainer className="slide-content-wrapper">
				<SlideContent>
					<Title $current={index}>{parseHTML(data?.title)}</Title>

					<Paragraph>{parseHTML(data?.description)}</Paragraph>

					<PrimaryButton href={data?.cta?.link} text={data?.cta?.text} />
				</SlideContent>
			</SlideContainer>
		</OutsideWrapper>
	);
};

const Title = styled.h1<{ $current: number }>`
	font-family: var(--font-sans);
	font-weight: var(--font-bold);
	font-size: 86px;
	line-height: 90px;
	color: var(--color-purple);
	margin-top: auto;
	margin-bottom: 20px;
	transform: translateY(50px);
	transition:
		transform 0.2s,
		opacity 0.2s;
	transition-delay: 0s;
	will-change: transform, opacity;
	opacity: 0;
	transform: translateY(60px);
	max-width: 600px;

	@media (max-width: 1260px) {
		font-size: 78px;
		line-height: 85px;
	}

	@media (max-width: 990px) {
		font-size: 68px;
		line-height: 75px;
	}

	@media (max-width: 900px) {
		transition: none !important;
	}

	@media (max-width: 600px) {
		font-size: 40px;
		line-height: 40px;
		max-width: 250px;
	}

	@media (max-width: 530px) {
		margin-top: auto;
	}

	${({ $current }) =>
		$current < 4
			? css`
					max-width: 400px;
				`
			: css`
					max-width: 500px;
				`}
`;

const Paragraph = styled.div`
	max-width: 300px;
	margin-bottom: 45px;
	font-size: 18px;
	line-height: 25px;
	font-family: var(--font-sans);

	color: var(--color-black);
	transform: translateY(50px);
	transition:
		transform 0.2s,
		opacity 0.2s;
	transition-delay: 0s;
	will-change: transform, opacity;
	opacity: 0;
	transform: translateY(50px);

	@media (max-width: 990px) {
		font-size: 17px;
	}

	@media (max-width: 900px) {
		transition: none !important;
	}

	@media (max-width: 600px) {
		font-size: 16px;
		line-height: 25px;
		max-width: 170px;
	}
`;

const SlideContent = styled.div`
	height: 100%;
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	position: relative;
	z-index: 900;

	button {
		margin-bottom: auto;
		transition:
			transform 0.2s,
			opacity 0.2s;
		transition-delay: 0s;
		will-change: transform, opacity;
		transform: translateY(50px);
		opacity: 0;

		@media (max-width: 900px) {
			transition: none !important;
		}

		@media (max-width: 530px) {
			margin-bottom: 80px;
			margin-top: auto;
		}
	}
`;

const scaleToLeft = keyframes`
  0% {
    transform: translateX(0) scale(1);
  }

  70% {
    transform: translateX(-70px) scale(1.15);
  }

  100% {
    transform: translateX(-140px) scale(1.05);
  }
`;

const scaleToLeftSmall = keyframes`
  0% {
    transform: translateX(0) scale(1);
  }

  70% {
    transform: translateX(-25px) scale(1.18);
  }

  100% {
    transform: translateX(-40px) scale(1.02);
  }
`;

const scaleFromLeft = keyframes`
  0% {
    transform: translateX(170px) scale(1.3);
  }

  100% {
    transform: translateX(0px) scale(1);
  }
`;

const scaleFromLeftSmall = keyframes`
  0% {
    transform: translateX(85px) scale(1.2);
  }

  100% {
    transform: translateX(0px) scale(1);
  }
`;

const fadeOut = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) translateZ(0) rotateZ(360deg);
  }

  100% {
    opacity: 0;
    transform: translateY(-30px) translateZ(0) rotateZ(360deg);
  }
`;

const fadeIn = keyframes`
  0% {
    opacity: 0;
    transform: translateY(30px) translateZ(0) rotateZ(360deg);
  }

  100% {
    opacity: 1;
    transform: translateY(0) translateZ(0) rotateZ(360deg);
  }
`;

const SlideContainer = styled.div`
	position: relative;
	width: 840px;
	height: 100%;
	margin: 0 auto;
	padding-left: 60px;
	padding-top: 50px;
	z-index: 400;

	@media (max-width: 1260px) {
		margin-left: 220px;
		margin-right: 0;
	}

	@media (max-width: 1050px) {
		margin-left: 190px;
		margin-right: 0;
	}

	@media (max-width: 900px) {
		width: 1320px;
		max-width: 95%;
		margin: 0 auto !important;
		margin-left: 0;
		padding-left: 0;
	}

	@media (max-width: 535px) {
		width: 100%;
		margin: 0;
		padding-left: 15px;
		padding-right: 15px;
		max-width: 100%;
	}
`;

const OutsideWrapper = styled.div<{ $active: number; $current: number }>`
	position: absolute;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100%;
	max-width: 100vw;
	overflow: hidden;
	z-index: 600;
	opacity: 1;

	${({ $active, $current }) =>
		$active === $current &&
		css`
			z-index: 1100;

			.slide-content-wrapper {
				z-index: 1100;
			}

			${Title} {
				opacity: 1;
				transform: translateY(0);
				transition-delay: 0.15s;

				@media (max-width: 900px) {
					transform: translateY(0) translateZ(0) rotateZ(360deg);
					transition: none !important;
					opacity: 1;
				}
			}

			${Paragraph} {
				opacity: 1;
				transform: translateY(0);
				transition-delay: 0.28s;

				@media (max-width: 900px) {
					transform: translateY(0) translateZ(0) rotateZ(360deg);
					transition: none !important;
					opacity: 1;
				}
			}

			button {
				opacity: 1;
				transform: translateY(0);
				transition-delay: 0.4s;

				@media (max-width: 900px) {
					transform: translateY(0) translateZ(0) rotateZ(360deg);
					transition: none !important;
					opacity: 1;
				}
			}
		`}

	&.hide-current-slide {
		z-index: 1000;

		.slide-content-wrapper {
			z-index: 1000;
		}

		${Title} {
			opacity: 0;
			transform: translateY(-60px);
			transition:
				transform 0.7s,
				opacity 0.7s;
			transition-delay: 0s;

			@media (max-width: 900px) {
				opacity: 1;
				transform: translateY(0) translateZ(0) rotateZ(360deg);
				animation: ${fadeOut} 0.35s 0s 1 forwards ease-in;
				transition: none !important;
				backface-visibility: hidden;
				perspective: 1000;
			}
		}

		${Paragraph} {
			opacity: 0;
			transform: translateY(-50px);
			transition:
				transform 0.7s,
				opacity 0.7s;
			transition-delay: 0.1s;

			@media (max-width: 900px) {
				opacity: 1;
				transform: translateY(0) translateZ(0) rotateZ(360deg);
				animation: ${fadeOut} 0.35s 0.12s forwards ease-in;
				transition: none !important;
				backface-visibility: hidden;
				perspective: 1000;
			}
		}

		button {
			opacity: 0;
			transform: translateY(-50px);
			transition:
				transform 0.7s,
				opacity 0.7s;
			transition-delay: 0.2s;

			@media (max-width: 900px) {
				opacity: 1;
				transform: translateY(0) translateZ(0) rotateZ(360deg);
				animation: ${fadeOut} 0.35s 0.21s 1 forwards ease-in;
				transition: none !important;
				backface-visibility: hidden;
				perspective: 1000;
			}
		}
	}

	&.show-new-slide {
		z-index: 900;

		.slide-content-wrapper {
			z-index: 900;
		}

		${Title} {
			opacity: 1;
			transform: translateY(0);
			transition:
				transform 0.5s,
				opacity 0.5s;
			transition-delay: 0.5s;

			@media (max-width: 900px) {
				opacity: 0;
				animation: ${fadeIn} 0.35s 0.52s forwards ease-out;
				transition: none !important;
				transform: translateZ(0) rotateZ(360deg);
				backface-visibility: hidden;
				perspective: 1000;
			}
		}

		${Paragraph} {
			opacity: 1;
			transform: translateY(0);
			transition:
				transform 0.5s,
				opacity 0.5s;
			transition-delay: 0.7s;

			@media (max-width: 900px) {
				opacity: 0;
				animation: ${fadeIn} 0.35s 0.58s forwards ease-out;
				transition: none !important;
				transform: translateZ(0) rotateZ(360deg);
				backface-visibility: hidden;
				perspective: 1000;
			}
		}

		button {
			opacity: 1;
			transform: translateY(0);
			transition:
				transform 0.5s,
				opacity 0.5s;
			transition-delay: 0.9s;

			@media (max-width: 900px) {
				opacity: 0;
				animation: ${fadeIn} 0.35s 0.65s forwards ease-out;
				transition: none !important;
				transform: translateZ(0) rotateZ(360deg);
				backface-visibility: hidden;
				perspective: 1000;
			}
		}
	}
`;

const Wrapper = styled.div<{ $active: number; $current: number }>`
	position: absolute;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100%;
	max-width: 100vw;
	overflow: hidden;
	z-index: 300;
	opacity: 1;
	clip-path: polygon(100% 0, 100% 0, 100% 100%, 100% 100%);
	transition: 0s;
	will-change: opacity, clip-path, z-index;

	${({ $active, $current }) =>
		$active === $current &&
		css`
			clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0 100%);
			opacity: 1;
			z-index: 1000;
		`}

	&.hide-current-slide {
		clip-path: polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%);
		opacity: 1;
		transition: clip-path 1.2s linear;
		z-index: 1000;

		@media (max-width: 600px) {
			transition: clip-path 1s linear;
		}

		.main-image {
			transform: translateX(0) scale(1);
			animation: ${scaleToLeft} 1.2s 1;

			@media (max-width: 600px) {
				animation: ${scaleToLeftSmall} 0.8s 1 forwards ease-out;
			}
		}
	}

	&.show-new-slide {
		clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0 100%);
		opacity: 1;
		transition: clip-path 1.2s linear;
		z-index: 900;

		@media (max-width: 600px) {
			transition: clip-path 1s linear;
		}

		.main-image {
			transform: translateX(170px) scale(1.3);
			animation: ${scaleFromLeft} 1s 1 forwards ease-out;

			@media (max-width: 600px) {
				animation: ${scaleFromLeftSmall} 0.8s 1 forwards ease-out;
			}
		}
	}
`;

const SlideItem = styled.div`
	width: 100vw;
	height: 100%;
	position: relative;
	z-index: 700;
`;

const SlideImages = styled.div`
	position: absolute;
	bottom: -1vh;
	right: -33vw;

	@media (max-width: 1023px) {
		right: -15vw;
	}

	@media (max-width: 767px) {
		bottom: 16vh;
	}

	img {
		display: block;
		width: 950px;

		@media (max-width: 1023px) {
			width: 700px;
		}

		@media (max-width: 767px) {
			width: 350px;
		}
	}
`;

const MessageImage = styled.img`
	width: 150.3px;
	position: absolute;
	bottom: 160px;
	left: -40px;

	@media (max-width: 700px) {
		left: -120px;
	}

	@media (max-width: 600px) {
		width: 60px;
		bottom: 110px;
		left: -40px;
	}
`;

export default Slide;
